import { useCallback, useEffect, useRef } from 'react';

interface UseAutoScrollOptions {
  /** The reference/ID of the element to scroll to */
  targetRef: string | null;
  /** Whether the data/content is ready for scrolling */
  isReady: boolean;
  /** The data attribute to look for when finding the target element (default: 'data-scroll-target') */
  dataAttribute?: string;
  /** Number of retry attempts if element is not found (default: 3) */
  retryAttempts?: number;
  /** Base delay between retry attempts in ms (default: 300) */
  retryDelay?: number;
  /** Scroll behavior options */
  scrollOptions?: ScrollIntoViewOptions;
  /** Callback to execute when target is not found (e.g., to clear query params) */
  onTargetNotFound?: () => void;
  /**
   * When true (default), re-scroll briefly while document layout height changes (e.g. lazy list
   * rows hydrating). Stops after layoutStabilizationMs or layoutStabilizationMaxRescrolls.
   */
  stabilizeLayout?: boolean;
  /** Max time to keep correcting scroll after layout shifts (default: 3000) */
  layoutStabilizationMs?: number;
  /** Max correction scrolls during stabilization (default: 25) */
  layoutStabilizationMaxRescrolls?: number;
}

/** Keys that scroll. A letter typed into the composer is not the reader taking over. */
const SCROLL_KEYS = new Set(['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' ', 'Spacebar']);

/**
 * Whether an event means the reader is scrolling for themselves.
 *
 * Wheel and touch always count. Keys only count when they scroll, and never
 * while the caret is in a field: after sending a message the timeline focuses
 * the message just sent, so the auto-scroll routinely starts with the user's
 * hands on the keyboard, and treating the next keystroke as "they took over"
 * would abandon the scroll they are waiting for.
 */
function isReaderScrollIntent(event: Event): boolean {
  if (event.type !== 'keydown') {
    return true;
  }

  const target = event.target as HTMLElement | null;
  if (target?.isContentEditable) {
    return false;
  }
  const tagName = target?.tagName;
  if (tagName === 'INPUT' || tagName === 'TEXTAREA' || tagName === 'SELECT') {
    return false;
  }

  return SCROLL_KEYS.has((event as KeyboardEvent).key);
}

/**
 * How long to keep watching for a displaced ancestor after a scroll, and how
 * often. A smooth scroll's duration scales with distance, so rather than guess
 * it we re-check across a window that comfortably outlasts one; `scrollend`
 * ends the watch early where the browser supports it.
 */
const SCROLL_SETTLE_WATCH_MS = 1500;
const SCROLL_SETTLE_POLL_MS = 100;

/** Prefer start/end near document edges so scrollIntoView can honor alignment (center often clamps poorly). */
function resolveScrollOptions(targetElement: HTMLElement, base: ScrollIntoViewOptions): ScrollIntoViewOptions {
  if (base.block !== 'center') {
    return base;
  }

  const rect = targetElement.getBoundingClientRect();
  const vh = window.innerHeight;
  const docBottom = document.documentElement.scrollHeight;
  const elTop = window.scrollY + rect.top;
  const elBottom = elTop + rect.height;
  const spaceBelow = docBottom - elBottom;
  const spaceAbove = elTop;

  let block: ScrollLogicalPosition = 'center';
  if (spaceBelow < vh * 0.35) {
    block = 'end';
  } else if (spaceAbove < vh * 0.25) {
    block = 'start';
  }

  return { ...base, block };
}

/**
 * Undo scrolling the browser applied to boxes the reader cannot scroll back.
 *
 * `scrollIntoView` scrolls *every* scrollable ancestor, and a box with
 * `overflow: hidden` (or `clip`) counts as one: it has no scrollbar and
 * ignores the wheel, but it can still be scrolled programmatically. When the
 * real scroll container cannot satisfy the request on its own — a target near
 * the top of a list, once scroll-padding is taken into account — the browser
 * takes the remainder out of one of those boxes, and the content it holds is
 * then displaced with no way to put it back.
 *
 * Anything the reader cannot scroll themselves, we do not leave scrolled.
 */
function resetUnscrollableAncestors(element: HTMLElement): void {
  // Walks past body to documentElement on purpose: an app shell that stops the
  // page scrolling with `overflow: hidden` on body or html turns those into
  // exactly the same trap, one level up. Only boxes computed as hidden/clip are
  // touched, so a document that genuinely scrolls is left alone.
  let node: HTMLElement | null = element.parentElement;

  while (node) {
    if (node.scrollTop !== 0 || node.scrollLeft !== 0) {
      const style = getComputedStyle(node);
      if (style.overflowY === 'hidden' || style.overflowY === 'clip') {
        node.scrollTop = 0;
      }
      if (style.overflowX === 'hidden' || style.overflowX === 'clip') {
        node.scrollLeft = 0;
      }
    }
    node = node.parentElement;
  }
}

/**
 * Reset displaced ancestors now, and keep resetting until the scroll settles.
 *
 * With `behavior: 'smooth'` the browser applies the ancestor displacement over
 * later frames, so a single reset on the same tick as scrollIntoView measures
 * zero and does nothing. Polling covers that without having to know how long
 * the animation runs — its duration scales with distance, and a fixed timeout
 * that guesses short leaves the displacement in place, which is the whole bug.
 * `scrollend` ends the watch early where it exists; for an instant scroll the
 * extra passes are no-ops on already-zero elements.
 */
function resetUnscrollableAncestorsWhenSettled(element: HTMLElement): () => void {
  resetUnscrollableAncestors(element);

  let stopped = false;
  const stop = (): void => {
    if (stopped) {
      return;
    }
    stopped = true;
    clearInterval(pollTimer);
    clearTimeout(watchTimer);
    document.removeEventListener('scrollend', onScrollEnd, true);
  };

  const pollTimer = setInterval(() => resetUnscrollableAncestors(element), SCROLL_SETTLE_POLL_MS);
  const watchTimer = setTimeout(stop, SCROLL_SETTLE_WATCH_MS);
  const onScrollEnd = (): void => {
    resetUnscrollableAncestors(element);
    stop();
  };
  document.addEventListener('scrollend', onScrollEnd, true);

  return stop;
}

/**
 * A generic hook for automatically scrolling to an element when conditions are met.
 * Handles page visibility, retry logic, and cleanup automatically.
 *
 * @example
 * // Timeline usage
 * useAutoScroll({
 *   targetRef: 'Communication/123',
 *   isReady: timelineGroups.length > 0,
 *   dataAttribute: 'data-timeline-item',
 *   onTargetNotFound: () => {
 *     // Optionally display an error message or do any action or cleanup after target is not found
 *   }
 * });
 *
 * @example
 * // Generic list usage
 * useAutoScroll({
 *   targetRef: 'item-456',
 *   isReady: items.length > 0,
 *   dataAttribute: 'data-item-id',
 *   retryAttempts: 2,
 *   scrollOptions: { behavior: 'auto', block: 'start' }
 * });
 *
 * @example
 * // Form field usage
 * useAutoScroll({
 *   targetRef: 'field-with-error',
 *   isReady: !submitting && errors.length > 0,
 *   dataAttribute: 'data-field-name',
 *   scrollOptions: { behavior: 'smooth', block: 'center' }
 * });
 */
export function useAutoScroll({
  targetRef,
  isReady,
  dataAttribute = 'data-scroll-target',
  retryAttempts = 3,
  retryDelay = 300,
  scrollOptions = {
    behavior: 'auto',
    block: 'center',
    inline: 'nearest',
  },
  onTargetNotFound,
  stabilizeLayout = true,
  layoutStabilizationMs = 3000,
  layoutStabilizationMaxRescrolls = 25,
}: UseAutoScrollOptions): void {
  const hasCompletedScroll = useRef(false);
  const lastScrollTargetRef = useRef<string | null>(null);

  // Helper function to find target element
  const findTargetElement = useCallback(
    (ref: string): HTMLElement | null => {
      // Try to find the element with the original reference
      let element = document.querySelector(`[${dataAttribute}="${ref}"]`);

      // If not found, try with decoded URI component in case of encoding mismatch
      if (!element) {
        try {
          const decodedRef = decodeURIComponent(ref);
          element = document.querySelector(`[${dataAttribute}="${decodedRef}"]`);
        } catch {
          // Ignore URI decoding errors
        }
      }

      return element as HTMLElement | null;
    },
    [dataAttribute]
  );

  useEffect(() => {
    if (!targetRef) {
      lastScrollTargetRef.current = null;
      hasCompletedScroll.current = true;
      return () => {};
    }

    if (!isReady) {
      return () => {};
    }

    // When the focus target changes, reset in this effect (before the hasCompletedScroll
    // guard). Previously a second effect ran after this one and cleared the flag too late,
    // so the scroll effect could bail out until unrelated deps changed.
    if (lastScrollTargetRef.current !== targetRef) {
      hasCompletedScroll.current = false;
      lastScrollTargetRef.current = targetRef;
    }

    if (hasCompletedScroll.current) {
      return () => {};
    }

    let cancelled = false;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    let visibilityListener: (() => void) | null = null;
    let resizeObserver: ResizeObserver | null = null;
    let stabilizationStopTimer: ReturnType<typeof setTimeout> | null = null;
    let layoutDebounceTimer: ReturnType<typeof setTimeout> | null = null;
    let userInputListener: ((event: Event) => void) | null = null;
    let cancelSettledReset: (() => void) | null = null;

    const cleanupStabilization = () => {
      if (resizeObserver) {
        resizeObserver.disconnect();
        resizeObserver = null;
      }
      if (stabilizationStopTimer) {
        clearTimeout(stabilizationStopTimer);
        stabilizationStopTimer = null;
      }
      if (layoutDebounceTimer) {
        clearTimeout(layoutDebounceTimer);
        layoutDebounceTimer = null;
      }
    };

    // Events that only a person produces. Deliberately not 'scroll': our own
    // scrollIntoView fires that, so listening for it would cancel the scroll
    // we just made.
    const USER_INPUT_EVENTS = ['wheel', 'touchmove', 'keydown'] as const;

    const removeUserInputListener = () => {
      if (userInputListener) {
        for (const eventName of USER_INPUT_EVENTS) {
          window.removeEventListener(eventName, userInputListener, true);
        }
        userInputListener = null;
      }
    };

    // Once the reader takes over, every pending correction is wrong: the
    // stabilization pass below re-scrolls for up to layoutStabilizationMs after
    // the landing, and without this it drags them back to the target while they
    // are trying to scroll away from it.
    const listenForUserInput = () => {
      if (userInputListener) {
        return;
      }
      userInputListener = (event: Event) => {
        if (!isReaderScrollIntent(event)) {
          return;
        }
        cancelled = true;
        // Treat the auto-scroll as done, not merely interrupted: leaving the
        // flag false would let a later re-render of this effect fire the same
        // deep-link scroll again and yank the reader back a second time.
        hasCompletedScroll.current = true;
        cleanupStabilization();
        cancelSettledReset?.();
        cancelSettledReset = null;
        if (timeoutId) {
          clearTimeout(timeoutId);
          timeoutId = null;
        }
        removeUserInputListener();
      };
      for (const eventName of USER_INPUT_EVENTS) {
        window.addEventListener(eventName, userInputListener, { capture: true, passive: true });
      }
    };

    const attemptScroll = (attempt: number) => {
      const targetElement = findTargetElement(targetRef);

      if (targetElement) {
        timeoutId = setTimeout(() => {
          if (cancelled) return;
          requestAnimationFrame(() => {
            if (cancelled) return;

            const scrollTargetIntoView = () => {
              const el = findTargetElement(targetRef);
              if (!el) return;
              el.scrollIntoView(resolveScrollOptions(el, scrollOptions));
              cancelSettledReset?.();
              cancelSettledReset = resetUnscrollableAncestorsWhenSettled(el);
            };

            scrollTargetIntoView();
            hasCompletedScroll.current = true;

            if (stabilizeLayout && typeof ResizeObserver !== 'undefined') {
              let rescrollCount = 0;
              let stabilizationEnded = false;

              const scheduleRescroll = () => {
                if (cancelled || stabilizationEnded || rescrollCount >= layoutStabilizationMaxRescrolls) return;
                if (layoutDebounceTimer) clearTimeout(layoutDebounceTimer);
                layoutDebounceTimer = setTimeout(() => {
                  layoutDebounceTimer = null;
                  if (cancelled || stabilizationEnded || rescrollCount >= layoutStabilizationMaxRescrolls) return;
                  const el = findTargetElement(targetRef);
                  if (!el) return;
                  rescrollCount += 1;
                  el.scrollIntoView(resolveScrollOptions(el, scrollOptions));
                  cancelSettledReset?.();
                  cancelSettledReset = resetUnscrollableAncestorsWhenSettled(el);
                }, 100);
              };

              resizeObserver = new ResizeObserver(scheduleRescroll);
              resizeObserver.observe(document.body);
              resizeObserver.observe(document.documentElement);

              stabilizationStopTimer = setTimeout(() => {
                stabilizationEnded = true;
                cleanupStabilization();
              }, layoutStabilizationMs);
            }
          });
        }, 150);
        return;
      }

      if (attempt >= retryAttempts) {
        onTargetNotFound?.();
        hasCompletedScroll.current = true;
        return;
      }

      // Try again after a delay, with increasing wait time
      timeoutId = setTimeout(() => attemptScroll(attempt + 1), retryDelay * attempt);
    };

    listenForUserInput();

    // Check if page is visible
    if (document.hidden) {
      // Page is not visible, wait for it to become visible
      visibilityListener = () => {
        if (!document.hidden && !hasCompletedScroll.current) {
          attemptScroll(1);
          document.removeEventListener('visibilitychange', visibilityListener!);
          visibilityListener = null;
        }
      };
      document.addEventListener('visibilitychange', visibilityListener);
    } else {
      // Page is visible, scroll immediately
      attemptScroll(1);
    }

    return () => {
      cancelled = true;
      cleanupStabilization();
      removeUserInputListener();
      cancelSettledReset?.();
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      if (visibilityListener) {
        document.removeEventListener('visibilitychange', visibilityListener);
      }
    };
  }, [
    targetRef,
    isReady,
    findTargetElement,
    retryAttempts,
    retryDelay,
    scrollOptions,
    onTargetNotFound,
    stabilizeLayout,
    layoutStabilizationMs,
    layoutStabilizationMaxRescrolls,
  ]);
}
